import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Camera,
  KeyRound,
  Power,
  RefreshCcw,
  ShieldCheck,
  Trash2,
  UserCog,
} from "lucide-react";
import { SwalWithTheme } from "../utils/sweetalert";
import {
  alternarStatusUsuario,
  atualizarUsuario,
  buscarUsuario,
  desativarDispositivoPushUsuario,
  encerrarSessaoUsuario,
  listarDispositivosPushUsuario,
  listarSessoesUsuario,
  resetarSenhaUsuario,
  uploadAvatarUsuario,
} from "../services/usuarioService";
import {
  Avatar,
  AvatarBadge,
  AvatarFallback,
  AvatarImage,
  Button,
  Card,
  DataTable,
  FormField,
  PageLoader,
  StatusBadge,
  getAvatarInitials,
} from "../components/ui";
import WebPushDevicesSection from "../components/WebPushDevicesSection";
import { validateAvatarFile } from "../utils/avatar";
import { formatDateTime, formatElapsedSeconds, formatSessionReason } from "../utils/session";

function buildSessionSummary(sessoes = []) {
  const ativa = sessoes.find((sessao) => sessao.ativa);
  const ultimaEncerrada = sessoes.find((sessao) => !sessao.ativa && sessao.logoutEm);
  return { ativa, ultimaEncerrada };
}

export default function UsuarioDetalhes() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [usuario, setUsuario] = useState(null);
  const [sessoes, setSessoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [pushDevices, setPushDevices] = useState([]);
  const [pushDevicesLoading, setPushDevicesLoading] = useState(true);
  const [sessionsPage, setSessionsPage] = useState(0);
  const [sessionsPageSize, setSessionsPageSize] = useState(10);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [disablePushDeviceBusyId, setDisablePushDeviceBusyId] = useState(null);
  const [form, setForm] = useState({
    nome: "",
    cargo: "user",
    senha: "",
    fotoPerfilUrl: "",
    supervisorIds: "",
  });

  const sessionSummary = useMemo(() => buildSessionSummary(sessoes), [sessoes]);
  const sessionTotalPages = useMemo(
    () => Math.max(1, Math.ceil(sessoes.length / sessionsPageSize)),
    [sessoes.length, sessionsPageSize]
  );
  const pagedSessions = useMemo(() => {
    const start = sessionsPage * sessionsPageSize;
    return sessoes.slice(start, start + sessionsPageSize);
  }, [sessoes, sessionsPage, sessionsPageSize]);
  const sessionsPagination = useMemo(
    () => ({
      page: sessionsPage,
      size: sessionsPageSize,
      totalPages: sessionTotalPages,
      totalElements: sessoes.length,
    }),
    [sessionTotalPages, sessionsPage, sessionsPageSize, sessoes.length]
  );

  useEffect(() => {
    setSessionsPage((current) => Math.min(current, Math.max(sessionTotalPages - 1, 0)));
  }, [sessionTotalPages]);

  async function carregarUsuario(showLoader = false) {
    if (showLoader) setLoading(true);
    try {
      const data = await buscarUsuario(id);
      setUsuario(data);
      setForm({
        nome: data.nome || "",
        cargo: data.cargo || "user",
        senha: "",
        fotoPerfilUrl: data.fotoPerfilUrl || "",
        supervisorIds: data.supervisorIds || "",
      });
    } catch {
      SwalWithTheme.error("Erro", "Usuario nao encontrado");
      navigate("/usuarios");
    } finally {
      if (showLoader) setLoading(false);
    }
  }

  async function carregarSessoes(showLoader = false) {
    if (showLoader) setSessionsLoading(true);
    try {
      const data = await listarSessoesUsuario(id);
      setSessoes(Array.isArray(data) ? data : []);
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao carregar sessoes";
      SwalWithTheme.error("Erro", message);
    } finally {
      if (showLoader) setSessionsLoading(false);
    }
  }

  async function carregarDispositivosPush(showLoader = false) {
    if (showLoader) setPushDevicesLoading(true);
    try {
      const data = await listarDispositivosPushUsuario(id);
      setPushDevices(Array.isArray(data) ? data : []);
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao carregar dispositivos Web Push";
      SwalWithTheme.error("Erro", message);
    } finally {
      if (showLoader) setPushDevicesLoading(false);
    }
  }

  async function carregarTudo() {
    setLoading(true);
    setSessionsLoading(true);
    setPushDevicesLoading(true);
    try {
      const [usuarioData, sessoesData, pushDevicesData] = await Promise.all([
        buscarUsuario(id),
        listarSessoesUsuario(id),
        listarDispositivosPushUsuario(id),
      ]);
      setUsuario(usuarioData);
      setForm({
        nome: usuarioData.nome || "",
        cargo: usuarioData.cargo || "user",
        senha: "",
        fotoPerfilUrl: usuarioData.fotoPerfilUrl || "",
        supervisorIds: usuarioData.supervisorIds || "",
      });
      setSessoes(Array.isArray(sessoesData) ? sessoesData : []);
      setPushDevices(Array.isArray(pushDevicesData) ? pushDevicesData : []);
    } catch {
      SwalWithTheme.error("Erro", "Não foi possível carregar os dados do usuário");
      navigate("/usuarios");
    } finally {
      setLoading(false);
      setSessionsLoading(false);
      setPushDevicesLoading(false);
    }
  }

  useEffect(() => {
    carregarTudo();
  }, [id]);

  function handleChange(event) {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function cancelarEdicao() {
    if (!usuario) return;
    setEditMode(false);
    setForm({
      nome: usuario.nome || "",
      cargo: usuario.cargo || "user",
      senha: "",
      fotoPerfilUrl: usuario.fotoPerfilUrl || "",
      supervisorIds: usuario.supervisorIds || "",
    });
  }

  async function handlePhotoChange(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const validationError = validateAvatarFile(file);
    if (validationError) {
      SwalWithTheme.error("Imagem invalida", validationError);
      return;
    }

    setAvatarUploading(true);
    try {
      const updated = await uploadAvatarUsuario(id, file);
      setUsuario(updated);
      setForm((prev) => ({ ...prev, fotoPerfilUrl: updated?.fotoPerfilUrl || "" }));
      setEditMode(false);
      SwalWithTheme.success("Sucesso", "Foto de perfil atualizada");
    } catch (error) {
      const message = error?.response?.data?.message || "Não foi possível enviar a imagem selecionada";
      SwalWithTheme.error("Erro", message);
    } finally {
      setAvatarUploading(false);
    }
  }

  async function salvar() {
    setSaving(true);
    try {
      const payload = {
        nome: form.nome,
        cargo: form.cargo,
        fotoPerfilUrl: form.fotoPerfilUrl,
        supervisorIds: form.supervisorIds,
      };

      if (form.senha?.trim()) {
        payload.senha = form.senha.trim();
      }

      await atualizarUsuario(id, payload);
      await carregarUsuario();
      setEditMode(false);
      SwalWithTheme.success("Sucesso", "Usuario atualizado");
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao atualizar usuario";
      SwalWithTheme.error("Erro", message);
    } finally {
      setSaving(false);
    }
  }

  async function alternarStatus() {
    const isAtivo = Boolean(usuario?.ativo);
    const actionLabel = isAtivo ? "desativar" : "ativar";

    const result = await SwalWithTheme.confirm(
      `Confirmar ${actionLabel}`,
      `Deseja ${actionLabel} este usuario?`,
      `Sim, ${actionLabel}`,
      "Cancelar"
    );
    if (!result.isConfirmed) return;

    try {
      await alternarStatusUsuario(id, isAtivo);
      await Promise.all([carregarUsuario(), carregarSessoes()]);
      SwalWithTheme.success("Sucesso", `Usuario ${isAtivo ? "desativado" : "ativado"}`);
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao alterar status";
      SwalWithTheme.error("Erro", message);
    }
  }

  async function resetarSenha() {
    const result = await SwalWithTheme.confirm(
      "Resetar senha",
      "Uma nova senha provisoria sera gerada para este usuario.",
      "Gerar nova senha",
      "Cancelar"
    );
    if (!result.isConfirmed) return;

    try {
      const data = await resetarSenhaUsuario(id);
      const novaSenha = data?.novaSenha || "(indisponivel)";
      await SwalWithTheme.fire({
        title: "Senha resetada",
        icon: "success",
        html: `<p>Senha provisoria:</p><pre style="font-size:16px">${novaSenha}</pre>`,
      });
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao resetar senha";
      SwalWithTheme.error("Erro", message);
    }
  }

  async function handleEncerrarSessao(sessao) {
    const result = await SwalWithTheme.confirm(
      "Encerrar sessao",
      `Deseja encerrar a sessao iniciada em ${formatDateTime(sessao.loginEm)}?`,
      "Encerrar sessao",
      "Cancelar"
    );
    if (!result.isConfirmed) return;

    try {
      await encerrarSessaoUsuario(id, sessao.id);
      await carregarSessoes();
      SwalWithTheme.success("Sucesso", "Sessao encerrada");
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao encerrar sessao";
      SwalWithTheme.error("Erro", message);
    }
  }

  async function handleDesativarDispositivoPush(device) {
    const result = await SwalWithTheme.confirm(
      "Desativar dispositivo",
      "Esse navegador deixara de receber Web Push para este usuario ate ser reativado.",
      "Desativar dispositivo",
      "Cancelar"
    );
    if (!result.isConfirmed) return;

    setDisablePushDeviceBusyId(device.id);
    try {
      await desativarDispositivoPushUsuario(id, device.id);
      await carregarDispositivosPush();
      SwalWithTheme.success("Sucesso", "Dispositivo Web Push desativado");
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao desativar dispositivo Web Push";
      SwalWithTheme.error("Erro", message);
    } finally {
      setDisablePushDeviceBusyId(null);
    }
  }

  if (loading) {
    return <PageLoader label="Carregando usuario..." />;
  }

  if (!usuario) {
    return <div className="py-16 text-center text-sm text-muted-foreground">Usuario nao encontrado.</div>;
  }

  const sessionColumns = [
    {
      key: "ativa",
      header: "Status",
      mobilePrimary: true,
      mobileOrder: 0,
      render: (value, row) => (
        <StatusBadge
          status={value ? "Ativa" : formatSessionReason(row.motivoEncerramento)}
          variant={value ? "success" : "default"}
        />
      ),
    },
    {
      key: "loginEm",
      header: "Login em",
      mobileOrder: 1,
      render: (value) => formatDateTime(value),
    },
    {
      key: "ultimaAtividadeEm",
      header: "Ultima atividade",
      mobileOrder: 2,
      render: (value) => formatDateTime(value),
    },
    {
      key: "tempoLogadoSegundos",
      header: "Tempo logado",
      mobileOrder: 3,
      render: (value) => formatElapsedSeconds(value),
    },
    {
      key: "ip",
      header: "IP",
      mobileOrder: 4,
      render: (value) => value || "N/A",
    },
  ];

  const sessionActions = [
    {
      icon: Power,
      title: "Encerrar",
      onClick: handleEncerrarSessao,
      hidden: (row) => !row.ativa,
    },
  ];

  return (
    <div className="space-y-4">
      <header className="page-header">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate("/usuarios")}>
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Button>
          <Button variant="outline" size="sm" onClick={carregarTudo}>
            <RefreshCcw className="h-4 w-4" />
            Atualizar
          </Button>
        </div>
        <h1 className="page-title">Detalhes do usuario</h1>
        <p className="page-subtitle">Edite dados, foto de perfil, seguranca e acompanhe o historico de sessoes.</p>
      </header>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <Card className="xl:col-span-2" title="Dados cadastrais" actions={editMode ? null : [<UserCog key="user-cog" className="h-4 w-4 text-muted-foreground" />]}>
          <div className="space-y-5">
            <div className="flex flex-col gap-4 rounded-2xl border border-border/60 bg-background/70 p-4 sm:flex-row sm:items-center">
              <Avatar className="h-28 w-28 border-border bg-muted text-2xl text-foreground">
                <AvatarImage src={form.fotoPerfilUrl || usuario.fotoPerfilUrl} alt={usuario.nome || "Usuario"} />
                <AvatarFallback>{getAvatarInitials(form.nome || usuario.nome)}</AvatarFallback>
                <AvatarBadge tone={sessionSummary.ativa ? "online" : "offline"} size="lg" />
              </Avatar>

              <div className="min-w-0 flex-1 space-y-2">
                <div>
                  <p className="text-lg font-semibold text-foreground">{usuario.nome}</p>
                  <p className="text-sm text-muted-foreground">{usuario.email}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge status={usuario.ativo ? "Ativo" : "Inativo"} variant={usuario.ativo ? "success" : "danger"} />
                  <StatusBadge status={usuario.cargo?.toLowerCase() === "admin" ? "Administrador" : "Comercial"} variant="info" />
                  {sessionSummary.ativa ? (
                    <StatusBadge status={`Online ha ${formatElapsedSeconds(sessionSummary.ativa.tempoLogadoSegundos)}`} variant="success" />
                  ) : (
                    <StatusBadge status="Offline" variant="danger" />
                  )}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium transition hover:bg-accent">
                  <Camera className="h-4 w-4" />
                  Foto
                  <input type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
                </label>
                {(form.fotoPerfilUrl || usuario.fotoPerfilUrl) && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setForm((prev) => ({ ...prev, fotoPerfilUrl: "" }));
                      if (!editMode) setEditMode(true);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                    Remover
                  </Button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField label="Nome" name="nome" value={form.nome} onChange={handleChange} disabled={!editMode || saving} />
              <FormField label="Email" type="email" value={usuario.email || ""} disabled />
              <FormField
                label="Cargo"
                type="select"
                name="cargo"
                value={form.cargo}
                onChange={handleChange}
                disabled={!editMode || saving}
                options={[
                  { value: "admin", label: "Administrador" },
                  { value: "user", label: "Comercial" },
                ]}
              />
              <FormField
                label="Supervisor ID(s) Join"
                name="supervisorIds"
                value={form.supervisorIds}
                onChange={handleChange}
                placeholder="Ex.: 3738, 21077"
                disabled={!editMode || saving}
              />
              <div className="space-y-1.5">
                <p className="text-sm font-medium">Ultima atividade</p>
                <div className="rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground">
                  {sessionSummary.ativa ? formatDateTime(sessionSummary.ativa.ultimaAtividadeEm) : "Sem sessao ativa"}
                </div>
              </div>
            </div>

            {editMode && (
              <FormField
                label="Nova senha (opcional)"
                type="password"
                name="senha"
                value={form.senha}
                onChange={handleChange}
                placeholder="Preencha apenas se quiser redefinir"
                disabled={saving}
              />
            )}

            <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-end">
              {!editMode ? (
                <Button variant="outline" onClick={() => setEditMode(true)}>
                  Editar
                </Button>
              ) : (
                <>
                  <Button variant="outline" onClick={cancelarEdicao} disabled={saving}>
                    Cancelar
                  </Button>
                  <Button onClick={salvar} disabled={saving}>
                    {saving ? "Salvando..." : "Salvar alteracoes"}
                  </Button>
                </>
              )}
            </div>
          </div>
        </Card>

        <div className="space-y-4">
          <Card title="Acoes da conta">
            <div className="space-y-2">
              <Button variant="outline" className="w-full justify-start" onClick={resetarSenha}>
                <KeyRound className="h-4 w-4" />
                Resetar senha
              </Button>
              <Button
                variant={usuario.ativo ? "danger" : "primary"}
                className="w-full justify-start"
                onClick={alternarStatus}
              >
                <ShieldCheck className="h-4 w-4" />
                {usuario.ativo ? "Desativar usuario" : "Ativar usuario"}
              </Button>
            </div>
          </Card>

          <Card title="Sistema">
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">ID</span>
                <span className="font-mono">{usuario.id}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Criado em</span>
                <span>{formatDateTime(usuario.dataCriacao)}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Atualizado em</span>
                <span>{formatDateTime(usuario.dataAtualizacao)}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Ultima desconexao</span>
                <span>{sessionSummary.ultimaEncerrada ? formatDateTime(sessionSummary.ultimaEncerrada.logoutEm) : "N/A"}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Supervisor ID(s)</span>
                <span>{usuario.supervisorIds || "N/A"}</span>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <Card title="Sessoes do usuario">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
          <p>O admin pode acompanhar atividade, tempo conectado e encerrar sessoes ativas deste usuario.</p>
          <Button variant="outline" size="sm" onClick={() => carregarSessoes(true)}>
            <RefreshCcw className="h-4 w-4" />
            Atualizar sessoes
          </Button>
        </div>

        <DataTable
          columns={sessionColumns}
          data={pagedSessions}
          actions={sessionActions}
          loading={sessionsLoading}
          pagination={sessionsPagination}
          onPageChange={setSessionsPage}
          onPageSizeChange={(nextSize) => {
            setSessionsPage(0);
            setSessionsPageSize(nextSize);
          }}
          enablePagination={sessoes.length > sessionsPageSize}
          emptyState="Nenhuma sessao registrada para este usuario"
        />
      </Card>

      <WebPushDevicesSection
        title="Dispositivos com Web Push"
        description="O admin pode revisar quais navegadores ainda recebem alertas e desligar dispositivos esquecidos."
        devices={pushDevices}
        loading={pushDevicesLoading}
        onRefresh={() => carregarDispositivosPush(true)}
        onDisableDevice={handleDesativarDispositivoPush}
        disableDeviceBusyId={disablePushDeviceBusyId}
        emptyState="Nenhum dispositivo com Web Push registrado para este usuario"
      />
    </div>
  );
}
